import { Component, OnInit, QueryList, TemplateRef, ViewChild, ViewChildren } from '@angular/core';
import { ILcaPatient } from 'src/app/_interfaces/lca-patients/i-lca-patient';
import { LcaPatientsService } from 'src/app/_services/lca/lca-patients.service';
import { City } from 'src/app/_models/city.model';
import { ColumnSortingDirective } from 'src/app/_directives/column-sorting.directive';
import { IColumnSortEvent } from 'src/app/_interfaces/i-column-sort-event';
import { DatePipe } from '@angular/common';
import { ISmartTablePagination } from 'src/app/_interfaces/smart-table/i-pagination';
import { ISmartTableMessage } from 'src/app/_interfaces/smart-table/i-message';
import { TableColumn } from '@swimlane/ngx-datatable';
import { Router } from '@angular/router';
import * as XLSX from 'xlsx';
import { PatientService } from 'src/app/_services/patient.service';
import { ILcaPatientDTO } from 'src/app/_models/patient.model';
import { PatientScreenTypeEnum } from 'src/app/enums/patient-screen-enum';

@Component({
  selector: 'app-lca-patients',
  templateUrl: './lca-patients.component.html',
  styleUrls: ['./lca-patients.component.scss'],
})
export class LcaPatientsComponent implements OnInit {
  pageTitle: string = 'LCA Patients';
  placeholder: string =
    'Search by patient name or referral code or phone number or LCA Id …';
  tableTitle: string = 'LCA Patients';
  theadArray = [
    'Name',
    'Phone Number',
    'Referral Code',
    'LCA Id',
    'Creation Date',
    'Valid Until',
  ];
  lcaPatientList: ILcaPatient[];
  filteredLcaPatientList: ILcaPatient[];
  cityList: City[] = [];
  currentPage: number = 1;
  pageSize: number = 10;
  totalPages: number;
  totalItems: number;
  searchString = '';
  noMatchingResult: boolean = true;
  invertSort: boolean = false;
  isSearchBtn: boolean = false;
  @ViewChildren(ColumnSortingDirective)
  headers: QueryList<ColumnSortingDirective>;
  @ViewChild("buttons") buttons: TemplateRef<any>;
  columns: TableColumn[]
  messages: ISmartTableMessage = {
    emptyMessage: 'Hmm, There is no LCA patients to show! 🥳',
    totalMessage: 'total',
    selectedMessage: ''
  };

  constructor(private lcaPatientsService: LcaPatientsService,
    private patientService: PatientService,
    private router: Router,
    private datePipe: DatePipe) { }

  ngOnInit(): void {

    this.patientService.setPatientScreenType(PatientScreenTypeEnum.LcaPatients);
    this.getLcaPatients();
    this.columns = [
      { prop: 'fullName', name: 'Name' },
      { prop: 'phone', name: 'Phone Number' },
      { prop: 'referralCode', name: 'Referral Code' },
      { prop: 'cardNumber', name: 'LCA ID' },
      {
        prop: 'creationDate', name: 'Creation Date',
        pipe: { transform: (val) => this.datePipe.transform(val, "MMM d, y, h:mm a") }
      },
      {
        prop: 'expirationDate', name: 'Valid Until',
        pipe: { transform: (val) => this.datePipe.transform(val, "MMM d, y, h:mm a") }
      },
    ]
  }

  compare = (v1: string | number, v2: string | number) =>
    v1 < v2 ? -1 : v1 > v2 ? 1 : 0;

  onSort({ column, direction }: IColumnSortEvent) {
    // resetting other headers
    this.headers.forEach((header) => {
      if (header.sortable !== column) {
        header.direction = '';
      }
    });

    // sorting countries
    if (direction == '' || column === '') {
      this.filteredLcaPatientList = [...this.lcaPatientList];
    } else {
      this.filteredLcaPatientList = [...this.lcaPatientList].sort(
        (a, b) => {
          const res = this.compare(a[column], b[column]);
          return direction === 'asc' ? res : -res;
        }
      );
    }
  }

  getLcaPatients() {
    this.lcaPatientsService
      .getLcaPatients(this.pageSize, this.currentPage)
      .subscribe(
        (response) => {
          this.noMatchingResult = false;

          this.lcaPatientList = response?.data?.items;

          this.filteredLcaPatientList = [...this.lcaPatientList];

          if (!this.filteredLcaPatientList.length) {
            this.noMatchingResult = true;
          }

          this.currentPage = response?.data?.pagination.currentPage;
          this.totalPages = response?.data?.pagination.totalPages;
          this.totalItems = response?.data?.pagination.totalItems;
        },
        (error) => {
          this.noMatchingResult = true;
          this.filteredLcaPatientList = [];
        }
      );
  }

  handleSearchLcaPatients() {
    this.lcaPatientsService
      .searchLcaPatient(this.searchString, this.pageSize, this.currentPage)
      .subscribe(
        (response) => {
          this.noMatchingResult = false;

          this.lcaPatientList = response?.data?.items;

          this.filteredLcaPatientList = [...this.lcaPatientList];

          if (!this.filteredLcaPatientList.length) {
            this.noMatchingResult = true;
          }

          this.currentPage = response?.data?.pagination.currentPage;
          this.totalPages = response?.data?.pagination.totalPages;
          this.totalItems = response?.data?.pagination.totalItems;
        },
        (error) => {
          this.noMatchingResult = true;
          this.filteredLcaPatientList = [];
        }
      );
  }

  onChangePage(pageDetails: ISmartTablePagination) {
    this.currentPage = pageDetails.offset + 1;
    if (this.isSearchBtn) {
      this.handleSearchLcaPatients();
    } else {
      this.getLcaPatients();
    }
  }

  onSearchKeyup(searchString) {
    if (!searchString) {
      this.isSearchBtn = false;
      this.currentPage = 1;
      this.lcaPatientList = [];
      this.getLcaPatients();
    }
  }

  onSearchSubmit(searchString) {
    this.isSearchBtn = true;
    this.currentPage = 1;
    this.lcaPatientList = [];
    this.searchString = searchString;

    if (searchString)
      this.handleSearchLcaPatients();
  }

  routeToPatientDetails(patient) {
    this.router.navigate(['/patient-details/', patient.selected[0].patientId]);
  }
  exportToExcel() {
    let data: ILcaPatientDTO[] = [];
    this.patientService.getExportToExcelLcaPatients().subscribe((res => {
      data = res.data;
      const header: string[][] = [[]];
      const ws = XLSX.utils.json_to_sheet(data);
      /* generate workbook and add the worksheet */
      const wb: XLSX.WorkBook = XLSX.utils.book_new();
      XLSX.utils.sheet_add_aoa(ws, header);

      //Starting in the second row to avoid overriding and skipping headers
      XLSX.utils.sheet_add_json(ws, data, { origin: 'A2', skipHeader: true });
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

      /* save to file */
      XLSX.writeFile(wb, `LCA patients - ${Date.now()}.xlsx`);
    }))

  }

}
